import React from 'react';
import { Button, Space } from 'antd';
import { LinkOutlined } from '@ant-design/icons';

export interface QuickCatalogLinksProps {
  vehicle:
    | { year?: number; make?: string; model?: string; vin?: string; engine_size?: string }
    | null
    | undefined;
}

/**
 * Quick links that open external parts catalogs / labor guide pre-scoped to the
 * current vehicle. URL schemes verified live (2026-06-28) against each site:
 *
 * - RockAuto: true vehicle prefill via lowercase `make,year,model,engine` catalog path.
 * - Everyone else: keyword search prefilled with "year make model engine" (no site
 *   accepts a VIN or a deep YMME-prefill param — fitment must be set on-page).
 */

// "year make model engine" keyword string, e.g. "2015 Ford Fusion 2.5L"
const ymmeKeywords = (v: QuickCatalogLinksProps['vehicle']): string =>
  [v?.year, v?.make, v?.model, v?.engine_size]
    .filter((p) => p != null && String(p).trim() !== '')
    .join(' ')
    .trim();

// RockAuto wants lowercase tokens with spaces as '+', comma-separated.
const rockAutoToken = (s: string | number | undefined): string =>
  String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '+');

const buildRockAutoUrl = (v: QuickCatalogLinksProps['vehicle']): string => {
  const parts = [v?.make, v?.year, v?.model, v?.engine_size]
    .map(rockAutoToken)
    .filter((t) => t !== '');
  // Need at least make,year,model to land on a catalog tree; otherwise fall back to search.
  if (parts.length < 3) {
    return `https://www.rockauto.com/en/partsearch/?partsearch=${encodeURIComponent(ymmeKeywords(v))}`;
  }
  return `https://www.rockauto.com/en/catalog/${parts.join(',')}`;
};

export const QuickCatalogLinks: React.FC<QuickCatalogLinksProps> = ({ vehicle }) => {
  const kw = ymmeKeywords(vehicle);
  const q = encodeURIComponent(kw);

  const links = [
    { label: 'RockAuto', href: buildRockAutoUrl(vehicle) },
    { label: 'Amazon', href: `https://www.amazon.com/s?k=${q}+parts` },
    { label: 'eBay', href: `https://www.ebay.com/sch/i.html?_nkw=${q}+parts` },
    { label: 'Advance', href: `https://shop.advanceautoparts.com/web/SearchResults?searchTerm=${q}` },
    { label: "O'Reilly", href: `https://www.oreillyauto.com/search?q=${q}` },
    { label: 'AutoZone', href: `https://www.autozone.com/searchresult?searchText=${q}` },
    { label: 'NAPA', href: `https://www.napaonline.com/en/search?text=${q}&referer=v2` },
    // Labor/repair info portals — login-gated, no URL vehicle prefill available.
    { label: 'ALLDATA', href: 'https://my.alldata.com' },
    { label: 'Chilton', href: 'https://badgerlink.dpi.wi.gov/resource/chiltonlibrary' },
  ];

  return (
    <Space wrap>
      {links.map((l) => (
        <Button
          key={l.label}
          icon={<LinkOutlined />}
          size="small"
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {l.label}
        </Button>
      ))}
    </Space>
  );
};

export default QuickCatalogLinks;
